import React, { useState, useEffect } from 'react';
import { X, Save, User } from 'lucide-react';
import { interviewApi, JobPosting } from '../../../services/api';
import { cache } from '../../../utils/CacheManager';
import { RecruiterCandidate, ErrorType } from '../../../types/recruiter';
import { interviewTypeFromLabel } from '../../../types/backendEnums';

// Recruiter 画面用の候補者型を使用
type Candidate = RecruiterCandidate;

interface InterviewForm {
  candidateId: string;
  startDate: string;
  endDate: string;
  type: string;
  interviewer: string;
  jobPostingId: string;
}

// プロップス定義（必要最小限）
interface InterviewModalProps {
  show: boolean;
  onClose: () => void;
  candidates: Candidate[];
  interviewForm: InterviewForm;
  setInterviewForm: (form: InterviewForm) => void;
  companyId: string;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  setError: (error: string | null) => void;
  setErrorType: (type: ErrorType) => void;
  handleError: (err: any, operation: string) => void;
  fetchInterviewsWithRetry: (useCache?: boolean) => Promise<void>;
  performOptimisticUpdate: (type: string, action: string, data: any) => void;
  confirmOptimisticUpdate: () => void;
  rollbackOptimisticUpdate: () => void;
  jobPostings: JobPosting[];
  jobPostingLoading: boolean;
}

const InterviewModal: React.FC<InterviewModalProps> = ({
  show,
  onClose,
  candidates,
  interviewForm,
  setInterviewForm,
  companyId,
  currentDate,
  setCurrentDate,
  setError,
  setErrorType,
  handleError,
  fetchInterviewsWithRetry,
  performOptimisticUpdate,
  confirmOptimisticUpdate,
  rollbackOptimisticUpdate,
  jobPostings,
  jobPostingLoading,
}) => {
  const preSelectedCandidate = candidates.find((c) => c.id === interviewForm.candidateId);

  // ローカルフォーム状態を使用して入力の独立性を保証
  const [localForm, setLocalForm] = useState({
    candidateId: '',
    startDate: '',
    endDate: '',
    type: '一次面接',
    interviewer: '',
    jobPostingId: '',
  });

  // モーダル内に表示するバリデーションエラー（親ページ最上部に出すと
  // モーダルの裏に隠れて見えないため、モーダル内にインライン表示する）
  const [formError, setFormError] = useState<string | null>(null);

  // 今日（ローカルタイム）のYYYY-MM-DD文字列
  // toISOString()はUTC変換されるため、JST 00:00〜08:59の間は前日の日付になってしまう。
  // ローカルの年月日から直接組み立てる。
  const now = new Date();
  const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // モーダルが開かれるたびにローカル状態をリセット（今日の日付をデフォルトに設定）
  useEffect(() => {
    if (show) {
      const initialCandidateId = interviewForm.candidateId || '';
      const today = new Date();

      setFormError(null);
      setLocalForm({
        candidateId: initialCandidateId,
        startDate: todayString,
        endDate: todayString,
        type: '一次面接',
        interviewer: '',
        jobPostingId: interviewForm.jobPostingId || '',
      });

      // モーダルが開かれた時のみ、現在の月のカレンダーに移動
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();

      // 現在表示中の月が今月でない場合のみ移動
      if (currentMonth !== todayMonth || currentYear !== todayYear) {
        setCurrentDate(new Date(todayYear, todayMonth, 1));
      }
    }
  }, [
    show,
    interviewForm.candidateId,
    interviewForm.jobPostingId,
    currentDate,
    setCurrentDate,
    todayString,
  ]);

  // 開始日の変更ハンドラ。過去日が入力された場合は弾いてモーダル内にエラー表示する。
  // （input の min に加え、キーボード直接入力でも過去日を選べないようにするための二重ガード）
  const handleStartDateChange = (value: string) => {
    if (value && value < todayString) {
      setFormError('面接日は今日以降の日付を選択してください');
      return;
    }
    setFormError(null);
    setLocalForm((prev) => ({
      ...prev,
      startDate: value,
      // 終了日が新しい開始日より前になる場合は開始日に揃える
      endDate: prev.endDate && prev.endDate < value ? value : prev.endDate,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    const errors: string[] = [];

    if (!localForm.candidateId) {
      errors.push('候補者を選択してください');
    }

    if (!localForm.startDate) {
      errors.push('面接日は必須です');
    } else if (localForm.startDate < todayString) {
      errors.push('面接日は今日以降の日付を選択してください');
    }

    if (!localForm.type) {
      errors.push('面接種別を選択してください');
    }

    if (errors.length > 0) {
      // 親ページ最上部だとモーダルの裏に隠れるため、モーダル内に表示する
      setFormError(errors.join(', '));
      return;
    }

    setFormError(null);

    const selectedCandidate = candidates.find((c) => c.id === localForm.candidateId);
    if (!selectedCandidate) {
      setFormError('選択された候補者が見つかりません');
      return;
    }

    if (!selectedCandidate.position || selectedCandidate.position === '未設定') {
      setFormError('候補者の職種が未設定です。候補者情報を編集して職種を設定してください');
      return;
    }

    const interviewData = {
      candidateId: localForm.candidateId,
      candidateName: selectedCandidate.name,
      candidateEmail: selectedCandidate.email,
      position: selectedCandidate.position,
      date: localForm.startDate,
      endDate: localForm.endDate,
      time: '14:00',
      duration: 60,
      type: localForm.type,
      interviewType: `InterviewType.${interviewTypeFromLabel(localForm.type)}` as const,
      jobPostingId: localForm.jobPostingId || null,
      companyId: companyId, // propsから取得した会社IDを送信
    };

    // 楽観的更新を実行
    performOptimisticUpdate('interview', 'add', interviewData);

    try {
      setError(null);
      setErrorType(null);

      const response = await interviewApi.createInterview(interviewData);
      if (response.success) {
        console.log('面接設定成功:', response.data);
        // 楽観的更新を確定
        confirmOptimisticUpdate();
        cache.invalidate('interviews');
        await fetchInterviewsWithRetry(false);
        handleClose();
      } else {
        throw new Error(response.message || response.error || '面接設定に失敗しました');
      }
    } catch (err) {
      // 楽観的更新をロールバック
      rollbackOptimisticUpdate();
      handleError(err, 'createInterview');
    }
  };

  const handleClose = () => {
    setFormError(null);
    onClose();
    setInterviewForm({
      candidateId: '',
      startDate: '',
      endDate: '',
      type: '一次面接',
      interviewer: '',
      jobPostingId: '',
    });
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      {/* 画面が低いと下端のボタンが画面外に出て押せなくなるため、
          パネルを画面高に収めて入力欄だけスクロールさせる */}
      <div className="bg-white rounded-lg w-full max-w-md max-h-full flex flex-col overflow-hidden">
        <div className="flex justify-between items-center px-6 pt-6 pb-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            {preSelectedCandidate ? `面接設定 - ${preSelectedCandidate.name}` : '面接設定'}
          </h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="space-y-4 overflow-y-auto px-6 pb-2 flex-1 min-h-0">
          {formError && (
            <div className="flex items-start gap-2 rounded-lg border-l-4 border-red-400 bg-red-50 p-3 text-red-800">
              <span className="flex-shrink-0">⚠️</span>
              <p className="text-sm font-medium">{formError}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">候補者</label>
            {preSelectedCandidate ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      {preSelectedCandidate.name}
                    </div>
                    <div className="text-xs text-gray-500">{preSelectedCandidate.position}</div>
                  </div>
                </div>
              </div>
            ) : (
              <select
                value={localForm.candidateId}
                onChange={(e) => setLocalForm((prev) => ({ ...prev, candidateId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="">候補者を選択してください</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} - {candidate.position}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">開始日</label>
            <input
              type="date"
              value={localForm.startDate}
              min={todayString}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">終了日</label>
            <input
              type="date"
              value={localForm.endDate}
              min={localForm.startDate || todayString}
              onChange={(e) => setLocalForm((prev) => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">面接種別</label>
            <select
              value={localForm.type}
              onChange={(e) => setLocalForm((prev) => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            >
              <option value="一次面接">一次面接</option>
              <option value="二次面接">二次面接</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">応募求人</label>
            <select
              value={localForm.jobPostingId}
              onChange={(e) => setLocalForm((prev) => ({ ...prev, jobPostingId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={jobPostingLoading}
            >
              <option value="">
                {jobPostingLoading
                  ? '応募求人を読み込み中...'
                  : jobPostings.length === 0
                    ? '応募求人が未登録です（空欄で保存可能）'
                    : '応募求人を選択してください'}
              </option>
              {jobPostings.map((posting) => (
                <option key={posting.id} value={posting.id}>
                  {posting.title}
                </option>
              ))}
            </select>
          </div>

          </div>

          {/* スクロールしても常に見える操作バー */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <Save size={16} />
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewModal;
